import { useEffect, useRef, useState } from "react";

/**
 * Share controls for a journal post.
 *
 * No third-party share widgets, deliberately. The usual buttons load scripts from
 * the platform and carry a tracker onto a page a student is reading; these are
 * plain links with query parameters plus the browser's own share sheet, so nothing
 * outside our origin runs and nobody is profiled for reading us. The CSP would
 * block a widget anyway.
 *
 * On a phone, navigator.share opens the real OS sheet (iMessage, WhatsApp,
 * Instagram, whatever the student actually uses), which is why it is offered first
 * when available. The explicit links stay for desktop, where the share sheet
 * mostly does not exist.
 */

type Props = {
  /** Absolute-from-root path, e.g. "/blog/some-post/". */
  path: string;
  title: string;
};

const SITE = "https://breakthetest.app";

export function ShareRow({ path, title }: Props) {
  const url = `${SITE}${path}`;
  const [copied, setCopied] = useState(false);
  const [canNativeShare, setCanNativeShare] = useState(false);
  const timer = useRef<number | null>(null);

  // Feature-detect after mount: the prerendered HTML must not assume either
  // branch, or a static snapshot would ship one browser's capabilities to all.
  useEffect(() => {
    setCanNativeShare(typeof navigator !== "undefined" && typeof navigator.share === "function");
    return () => {
      if (timer.current) window.clearTimeout(timer.current);
    };
  }, []);

  const flashCopied = () => {
    setCopied(true);
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setCopied(false), 2200);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      flashCopied();
    } catch {
      // Clipboard blocked (insecure context, permissions). Select-and-prompt is
      // the only fallback that works everywhere without a hidden textarea hack.
      window.prompt("Copy this link:", url);
    }
  };

  const nativeShare = async () => {
    try {
      await navigator.share({ title, url });
    } catch {
      // AbortError when the user dismisses the sheet. Nothing to report.
    }
  };

  const q = encodeURIComponent;
  const links = [
    { label: "X", href: `https://x.com/intent/tweet?text=${q(title)}&url=${q(url)}` },
    { label: "Reddit", href: `https://www.reddit.com/submit?url=${q(url)}&title=${q(title)}` },
    {
      label: "WhatsApp",
      href: `https://api.whatsapp.com/send?text=${q(`${title} ${url}`)}`,
    },
  ];

  return (
    <div style={wrap}>
      <span style={label} aria-hidden>
        Share
      </span>

      {canNativeShare && (
        <button type="button" onClick={nativeShare} style={btn} aria-label={`Share "${title}"`}>
          Share
        </button>
      )}

      {links.map((l) => (
        <a
          key={l.label}
          href={l.href}
          target="_blank"
          // noreferrer as well as noopener: the target should not be told which of
          // our pages a student came from.
          rel="noopener noreferrer"
          style={btn}
          aria-label={`Share "${title}" on ${l.label}`}
        >
          {l.label}
        </a>
      ))}

      <button type="button" onClick={copy} style={btn} aria-label={`Copy link to "${title}"`}>
        {copied ? "Link copied" : "Copy link"}
      </button>

      {/* Announced to screen readers; the button label alone changes silently. */}
      <span role="status" aria-live="polite" style={srOnly}>
        {copied ? "Link copied to clipboard" : ""}
      </span>
    </div>
  );
}

const wrap: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  gap: 10,
  marginTop: 40,
  paddingTop: 20,
  borderTop: "1px solid var(--border-line)",
};

const label: React.CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  color: "var(--fg-2)",
  marginRight: 4,
};

const btn: React.CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  color: "var(--fg-1)",
  background: "var(--bg-1)",
  border: "1px solid var(--border-line)",
  borderRadius: 4,
  padding: "0 14px",
  minHeight: 44,
  display: "inline-flex",
  alignItems: "center",
  textDecoration: "none",
  cursor: "pointer",
};

const srOnly: React.CSSProperties = {
  position: "absolute",
  width: 1,
  height: 1,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
};
